import { createRouter, createWebHistory } from "vue-router";
import { useLangStore } from "@/stores/lang.js";

const HomeView = () => import("@/views/HomeView.vue");
const MonitorView = () => import("@/views/MonitorView.vue");
const MarketView = () => import("@/views/MarketView.vue");
const TaskCenterView = () => import("@/views/TaskCenterView.vue");
const BBrokerDeployView = () => import("@/views/BBrokerDeployView.vue");
const ShopView = () => import("@/views/ShopView.vue");
const GuideView = () => import("@/views/GuideView.vue");
const FlapMintCreateView = () => import("@/views/FlapMintCreateView.vue");
const FlapMintLiveView = () => import("@/views/FlapMintLiveView.vue");
const FlapMintDetailView = () => import("@/views/FlapMintDetailView.vue");
const NotFoundView = () => import("@/views/NotFoundView.vue");

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/token/:address", name: "token-detail", component: () => import("@/views/TokenDetailView.vue"),
      meta: { titleZh: "封心 — 代币详情", titleEn: "Fengxin — Token Details" },
    },
    {
      path: "/admin/health", name: "admin-health", component: () => import("@/views/AdminHealthView.vue"),
      meta: { titleZh: "封心 — 后台健康", titleEn: "Fengxin — Admin Health" },
    },
    {
      path: "/tasks", name: "tasks", component: TaskCenterView,
      meta: { titleZh: "封心 — 任务中心", titleEn: "Fengxin — Task Center" },
    },
    {
      path: "/",
      name: "home",
      component: HomeView,
      meta: {
        titleZh: "封心 — Flap 金库",
        titleEn: "Fengxin — Flap Vaults",
      },
    },
    {
      path: "/monitor",
      name: "monitor",
      component: MonitorView,
      meta: {
        titleZh: "封心 — 工厂监听",
        titleEn: "Fengxin — Factory Monitor",
      },
    },
    {
      path: "/market",
      name: "market",
      component: MarketView,
      meta: {
        titleZh: "封心 — 市值工具",
        titleEn: "Fengxin — Market Tools",
      },
    },
    {
      path: "/shop",
      name: "shop",
      component: ShopView,
      meta: {
        titleZh: "封心 — 金库市场",
        titleEn: "Fengxin — Vault Catalog",
      },
    },
    {
      path: "/guide",
      name: "usage-guide",
      component: GuideView,
      meta: {
        titleZh: "封心 — 使用指南",
        titleEn: "Fengxin — Usage Guide",
      },
    },
    {
      path: "/mint",
      name: "mint-create",
      component: FlapMintCreateView,
      meta: {
        titleZh: "封心 — 公平 Mint",
        titleEn: "Fengxin — Fair Mint",
      },
    },
    {
      path: "/mint/live",
      name: "mint-live",
      component: FlapMintLiveView,
      meta: {
        titleZh: "封心 — Mint 监听",
        titleEn: "Fengxin — Mint Live",
      },
    },
    {
      path: "/mint-monitor",
      redirect: { name: "mint-live" },
    },
    {
      path: "/mint/:vault",
      name: "mint-detail",
      component: FlapMintDetailView,
      meta: {
        titleZh: "封心 — 公平 Mint 详情",
        titleEn: "Fengxin — Fair Mint Detail",
      },
      props: true,
    },
    {
      path: "/deploy/bbk-v3",
      name: "deploy-bbk-v3",
      component: BBrokerDeployView,
      meta: {
        titleZh: "封心 — 币股NFT 站内部署",
        titleEn: "Fengxin — Coin-Stock NFT Deploy",
      },
      props: { vaultId: "coin-stock-nft-bbk-v3" },
    },
    {
      path: "/deploy/:vaultId",
      name: "deploy-stock",
      component: BBrokerDeployView,
      meta: {
        titleZh: "封心 — 站内部署",
        titleEn: "Fengxin — On-site Deploy",
      },
      props: true,
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: NotFoundView,
      meta: {
        titleZh: "封心 — 页面未找到",
        titleEn: "Fengxin — Page Not Found",
      },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.name === "usage-guide" && from.name === "usage-guide" && !to.hash) return false;
    if (to.hash) {
      if (to.name === "monitor") return { top: 0 };
      const target = document.getElementById(to.hash.slice(1));
      const top = target ? parseFloat(getComputedStyle(target).scrollMarginTop) || 0 : 0;
      return { el: to.hash, top, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' };
    }
    return { top: 0 };
  },
});

// Preserve shared links to cards moved from the homepage into the catalog.
router.beforeEach((to) => {
  if (to.name === "home" && to.hash.startsWith("#vault-")) {
    return { name: "shop", query: { vault: to.hash.slice(7) }, replace: true };
  }
});

router.afterEach((to) => {
  try {
    const lang = useLangStore();
    if (to.meta?.titleZh) {
      document.title = lang.t(to.meta.titleZh, to.meta.titleEn || to.meta.titleZh);
    }
  } catch {
    if (to.meta?.titleZh) document.title = String(to.meta.titleZh);
  }
});

export default router;
